import { lighten } from '@mui/material';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { FC, ReactNode } from 'react';

type TActionIconButtonColor = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

interface ActionIconButtonProps extends Omit<IconButtonProps, 'children' | 'color'> {
  icon: ReactNode;
  tooltip?: string;
  color?: TActionIconButtonColor;
  /** When true, renders without the tinted background. */
  noBg?: boolean;
  /**
   * Renders the button as a real `<a>` instead of a `<button>`, for actions that
   * navigate. Worth the extra prop over an `onClick` that calls `window.open`: an
   * anchor keeps middle-click, cmd-click and the browser's own "open in new tab"
   * context menu, none of which a click handler can offer.
   */
  href?: string;
  target?: string;
  rel?: string;
}

export const ActionIconButton: FC<ActionIconButtonProps> = ({
  icon,
  tooltip,
  color = 'primary',
  noBg = false,
  href,
  target,
  rel,
  sx,
  ...rest
}) => {
  // Only switch element when a destination is given, so every existing call site
  // keeps rendering a plain <button>.
  const anchorProps = href ? ({ component: 'a' as const, href, target, rel } as const) : {};

  const button = (
    <IconButton
      size="small"
      {...anchorProps}
      sx={{
        p: 1.3,
        borderRadius: 1.5,
        // Was `lighten(main, 0.25)`, which put the glyph at 2.65:1 against its own
        // tinted background — under the 3:1 WCAG floor for non-text UI, and the
        // reason these buttons read as washed out. `main` on the same tint is 3.9:1.
        color: theme => theme.palette[color].main,
        backgroundColor: noBg ? 'transparent' : theme => lighten(theme.palette[color].main, 0.85),
        '&:hover': {
          backgroundColor: noBg ? 'transparent' : theme => lighten(theme.palette[color].main, 0.78),
        },
        '&.Mui-disabled': {
          color: 'action.disabled',
          backgroundColor: noBg ? 'transparent' : 'action.disabledBackground',
        },
        ...sx,
      }}
      {...rest}
    >
      {icon}
    </IconButton>
  );

  if (!tooltip) {
    return button;
  }

  // A disabled button fires no events, so MUI needs a live wrapper element to
  // anchor the tooltip to (otherwise it logs a "disabled button child" warning).
  return (
    <Tooltip title={tooltip}>
      <span style={{ display: 'inline-flex' }}>{button}</span>
    </Tooltip>
  );
};
